import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Badge } from '../../components/ui/Badge.js';
import { ConfirmModal } from '../../components/ui/ConfirmModal.js';
import { useApi } from '../../hooks/useApi.js';
import { ApiError } from '../../lib/api.js';
import { ordenarCatalogo } from '../../lib/catalogos.js';
import type { CatalogoItem } from '../clientes/types.js';

interface CatalogoCardProps {
  tipo: string;
  titulo: string;
  items: CatalogoItem[];
  isLoading: boolean;
  onToast: (type: 'success' | 'error', message: string) => void;
  // Los siguientes props permiten reutilizar este mismo componente para
  // catálogos con un endpoint distinto al genérico /catalogos/:tipo/items
  // (Prompt AA — Áreas usa /catalogos/area, tabla propia, sin DELETE físico).
  // Todos opcionales: los 4 catálogos originales (Giro/Origen/Plataforma/Tiers)
  // no necesitan pasarlos y siguen funcionando exactamente igual.
  endpointBase?: string;
  puedeEliminar?: boolean;
  invalidateKeys?: string[][];
  // Controla si se muestran los controles de gestión (agregar/editar/eliminar/
  // toggle) o solo la lista en modo lectura — para roles que pueden VER este
  // catálogo en la página pero no gestionarlo (ver Parte C, Prompt AA).
  puedeGestionar?: boolean;
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

export function CatalogoCard({
  tipo,
  titulo,
  items,
  isLoading,
  onToast,
  endpointBase,
  puedeEliminar = true,
  invalidateKeys,
  puedeGestionar = true,
}: CatalogoCardProps) {
  const { post, patch, delete: del } = useApi();
  const queryClient = useQueryClient();
  const base = endpointBase ?? `/catalogos/${tipo}/items`;
  const keysAInvalidar = invalidateKeys ?? [['catalogos-admin'], ['catalogo']];

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [addValue, setAddValue] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<CatalogoItem | null>(null);

  const itemsOrdenados = useMemo(() => ordenarCatalogo(items, tipo), [items, tipo]);

  function invalidarTodo() {
    for (const key of keysAInvalidar) queryClient.invalidateQueries({ queryKey: key });
  }

  const crearMutation = useMutation({
    mutationFn: (valor: string) => post(base, { valor }),
    onSuccess: () => {
      invalidarTodo();
      setAddOpen(false);
      setAddValue('');
      onToast('success', `"${titulo}" actualizado: ítem agregado.`);
    },
    onError: (err: unknown) => onToast('error', errorMessage(err, 'No fue posible agregar el ítem.')),
  });

  const editarMutation = useMutation({
    mutationFn: ({ id, valor }: { id: string; valor: string }) => patch(`${base}/${id}`, { valor }),
    onSuccess: () => {
      invalidarTodo();
      setEditingId(null);
      onToast('success', 'Ítem actualizado correctamente.');
    },
    onError: (err: unknown) => onToast('error', errorMessage(err, 'No fue posible editar el ítem.')),
  });

  const toggleMutation = useMutation({
    mutationFn: (id: string) => patch(`${base}/${id}/toggle`),
    onSuccess: () => invalidarTodo(),
    onError: (err: unknown) => onToast('error', errorMessage(err, 'No fue posible cambiar el estatus del ítem.')),
  });

  const eliminarMutation = useMutation({
    mutationFn: (id: string) => del(`${base}/${id}`),
    onSuccess: () => {
      invalidarTodo();
      setDeleteTarget(null);
      onToast('success', 'Ítem eliminado correctamente.');
    },
    onError: (err: unknown) => {
      setDeleteTarget(null);
      onToast('error', errorMessage(err, 'No fue posible eliminar el ítem.'));
    },
  });

  function confirmarAgregar() {
    const valor = addValue.trim();
    if (!valor) return;
    crearMutation.mutate(valor);
  }

  function iniciarEdicion(item: CatalogoItem) {
    setEditingId(item.id);
    setEditValue(item.valor);
  }

  function confirmarEdicion() {
    const valor = editValue.trim();
    if (!valor || !editingId) return;
    editarMutation.mutate({ id: editingId, valor });
  }

  return (
    <div className="cat-card">
      <div className="cat-card-hdr">
        <h3>{titulo}</h3>
        {puedeGestionar && (
          <button className="btn btn-outline btn-sm" onClick={() => setAddOpen(o => !o)}>
            + Agregar
          </button>
        )}
      </div>

      <div className="cat-items">
        {isLoading && <div style={{ padding: 14, fontSize: 13, color: 'var(--muted)' }}>Cargando…</div>}
        {!isLoading && items.length === 0 && (
          <div style={{ padding: 14, fontSize: 13, color: 'var(--muted)' }}>Sin ítems en este catálogo.</div>
        )}
        {itemsOrdenados.map(item => {
          const editing = puedeGestionar && editingId === item.id;
          return (
            <div className="cat-item" key={item.id} style={item.activo ? undefined : { opacity: 0.55 }}>
              {editing ? (
                <div className="cat-item-edit-row">
                  <input
                    type="text"
                    value={editValue}
                    autoFocus
                    onChange={e => setEditValue(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') confirmarEdicion();
                      if (e.key === 'Escape') setEditingId(null);
                    }}
                  />
                  <button className="cat-edit-action cat-edit-ok" title="Guardar" onClick={confirmarEdicion} disabled={editarMutation.isPending}>
                    ✓
                  </button>
                  <button className="cat-edit-action cat-edit-cancel-btn" title="Cancelar" onClick={() => setEditingId(null)}>
                    ✕
                  </button>
                </div>
              ) : (
                <div className="cat-item-edit-row">
                  <span className="cat-item-label">{item.valor}</span>
                  {!item.activo && <Badge tone="neutral">Inactivo</Badge>}
                  {puedeGestionar && (
                    <div style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
                      <button className="cat-action-btn" title="Editar" onClick={() => iniciarEdicion(item)}>
                        ✎
                      </button>
                      {puedeEliminar && (
                        <button className="cat-action-btn del" title="Eliminar" onClick={() => setDeleteTarget(item)}>
                          ✕
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
              <label className="tog" style={{ marginLeft: 10 }} title={item.activo ? 'Desactivar' : 'Activar'}>
                <input
                  type="checkbox"
                  checked={item.activo}
                  disabled={!puedeGestionar}
                  onChange={() => toggleMutation.mutate(item.id)}
                />
                <span className="tog-slider" />
              </label>
            </div>
          );
        })}
      </div>

      {puedeGestionar && addOpen && (
        <div className="cat-add-row">
          <input
            type="text"
            placeholder="Nuevo elemento..."
            value={addValue}
            autoFocus
            onChange={e => setAddValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') confirmarAgregar();
            }}
          />
          <button className="btn btn-primary btn-sm" onClick={confirmarAgregar} disabled={crearMutation.isPending}>
            Agregar
          </button>
          <button
            className="btn btn-outline btn-sm"
            onClick={() => {
              setAddOpen(false);
              setAddValue('');
            }}
          >
            ✕
          </button>
        </div>
      )}

      <ConfirmModal
        open={deleteTarget !== null}
        title="¿Eliminar elemento?"
        body={deleteTarget ? `Se eliminará "${deleteTarget.valor}" del catálogo "${titulo}". Esta acción no se puede deshacer.` : ''}
        confirmLabel="Eliminar"
        danger
        onConfirm={() => deleteTarget && eliminarMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
